/**
 * The live activity tape (docs/DESIGN.md "FOMO layer"): a slim one-line ticker under the header from 1024px, and a
 * "live" row at the top of the feed below 768px. Both read the shared tape (`useActivity`, one SSE connection).
 * New items slide in from the left (off under reduced motion); hovering or focusing the ticker freezes it and counts
 * what arrived meanwhile, so nothing moves under the pointer.
 */
import { useRef, useState } from 'react'
import type { ActivityItem, ActivityKind } from '@algoboo/schema/api'
import { Money, UiLink, cx, explorerUrl, timeAgo } from '@algoboo/ui'
import { AgentAvatar } from '../widgets'
import { useActivity, type ActivityStatus } from './data'
import { useNow } from '../../lib/now'

const KIND_LABEL: Record<ActivityKind, string> = {
  trade: 'Trade',
  launch: 'Launch',
  epoch_closed: 'Week closed',
  reward_claimed: 'Claim',
  agent_activated: 'New agent',
  milestone: 'Milestone',
  challenge: 'Challenge',
}

function LiveDot({ status, paused }: { status: ActivityStatus; paused?: boolean }) {
  const live = status === 'live' && !paused
  return <span aria-hidden="true" className={cx('h-1.5 w-1.5 shrink-0 rounded-full', live ? 'rh-live-dot bg-gain' : 'bg-line-strong')} />
}

/** One tape entry: avatar · kind · summary · size · age. Opens the item's page, else its transaction. */
export function ActivityEntry({ item, fresh, now, className }: { item: ActivityItem; fresh: boolean; now: number; className?: string }) {
  const body = (
    <>
      {item.agent && <AgentAvatar handle={item.agent.handle} name={item.agent.name} avatar={item.agent.avatar} avatarUrl={item.agent.avatarUrl} size={16} />}
      <span className="shrink-0 text-[10.5px] font-medium uppercase tracking-[0.06em] text-ink-3">{KIND_LABEL[item.kind]}</span>
      <span className="min-w-0 truncate text-ink-2">{item.summary}</span>
      {item.amountUsd && <Money value={item.amountUsd} compact className="shrink-0 font-mono text-[11.5px] text-ink" />}
      <time dateTime={item.occurredAt} className="shrink-0 font-mono text-[11px] text-ink-4">
        {timeAgo(item.occurredAt, now)}
      </time>
    </>
  )
  const cls = cx('inline-flex min-w-0 max-w-[420px] items-center gap-1.5 rounded-[6px] px-1 text-[12.5px] leading-none hover:bg-sunken', fresh && 'rh-tape-in', className)
  const attrs = { 'data-testid': 'activity-item', 'data-kind': item.kind, 'data-agent': item.agent?.handle ?? '', 'data-fresh': fresh ? 'true' : 'false', title: item.summary }
  if (item.href) {
    return (
      <UiLink href={item.href} className={cls} {...attrs}>
        {body}
      </UiLink>
    )
  }
  if (item.txHash) {
    return (
      <a href={explorerUrl('tx', item.txHash)} target="_blank" rel="noreferrer" className={cls} {...attrs}>
        {body}
      </a>
    )
  }
  return (
    <span className={cls} {...attrs}>
      {body}
    </span>
  )
}

/** Desktop tape under the header (hidden below 1024px). */
export function ActivityTicker() {
  const { items, status, fresh } = useActivity()
  const [frozen, setFrozen] = useState<readonly ActivityItem[] | null>(null)
  const hold = useRef(false)
  const freeze = () => {
    if (hold.current) return
    hold.current = true
    setFrozen(items)
  }
  const thaw = () => {
    hold.current = false
    setFrozen(null)
  }
  const shown = (frozen ?? items).slice(0, 14)
  const waiting = frozen ? items.filter((i) => !frozen.some((f) => f.id === i.id)).length : 0
  const now = useNow(30_000)
  if (items.length === 0 && status !== 'live') return null
  return (
    <div
      data-testid="activity-ticker"
      data-status={status}
      data-paused={frozen ? 'true' : 'false'}
      role="region"
      aria-label="Live activity"
      className="hidden border-b border-line bg-bg lg:block"
      onMouseEnter={freeze}
      onMouseLeave={thaw}
      onFocus={freeze}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) thaw()
      }}
    >
      <div className="flex h-8 items-center gap-3 px-6">
        <span className="inline-flex w-[64px] shrink-0 items-center gap-1.5 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3" role="status">
          <LiveDot status={status} paused={!!frozen} />
          {frozen ? (waiting > 0 ? `+${waiting}` : 'paused') : status === 'live' ? 'live' : status === 'off' ? 'off' : '…'}
        </span>
        {shown.length === 0 ? (
          <span className="text-[12px] text-ink-3">Waiting for the first trade on the floor.</span>
        ) : (
          <ol aria-live="off" className="flex min-w-0 flex-1 items-center gap-4 overflow-hidden [mask-image:linear-gradient(to_right,#000_88%,transparent)]">
            {shown.map((it) => (
              <li key={it.id} className="flex shrink-0 items-center">
                <ActivityEntry item={it} fresh={!frozen && fresh.has(it.id)} now={now} />
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  )
}

/** Mobile: the newest events as one swipeable row at the top of the feed (hidden from 768px). */
export function ActivityLiveRow() {
  const { items, status, fresh } = useActivity()
  const now = useNow(30_000)
  if (items.length === 0) return null
  return (
    <section data-testid="activity-live-row" aria-label="Live activity" className="rh-card -mx-1 overflow-hidden rounded-xl md:hidden">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
        <LiveDot status={status} /> live on the floor
      </div>
      <ol className="flex snap-x gap-2 overflow-x-auto px-2 py-2 [scrollbar-width:none]">
        {items.slice(0, 10).map((it) => (
          <li key={it.id} className="snap-start">
            <ActivityEntry item={it} fresh={fresh.has(it.id)} now={now} className="h-8 max-w-[280px] border border-line bg-surface px-2" />
          </li>
        ))}
      </ol>
    </section>
  )
}
